<template>
  <v-container fluid>
    <route-plan-file-upload-detail-modal 
      ref="detailModal"
      @reprocess="handleReprocess"
    />
    
    <div class="d-flex justify-space-between align-center mb-4">
      <div>
        <h1 class="text-h4 mb-2">{{ $t('route_plan_file_upload_history') }}</h1>
        <p class="text-subtitle-1 text--secondary mb-0">
          {{ $t('view_and_manage_route_plan_file_upload_records') }}
        </p>
      </div>
      
      <div class="d-flex align-center">
        <v-btn
          outlined
          color="primary"
          @click="refreshData"
          :loading="loading"
          class="mr-2">
          <v-icon left>mdi-refresh</v-icon>
          {{ $t('refresh') }}
        </v-btn>
        
        <v-btn
          color="primary"
          to="/route-plans">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('back_to_route_plans') }}
        </v-btn>
      </div>
    </div>

    <!-- Statistics Cards -->
    <v-row class="mb-4" v-if="statistics">
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h2 primary--text">{{ statistics.total_imports }}</div>
            <div class="text-caption text--secondary">{{ $t('total_file_uploads') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h2 warning--text">{{ statistics.processing_imports }}</div>
            <div class="text-caption text--secondary">{{ $t('processing') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h2 success--text">{{ statistics.completed_imports }}</div>
            <div class="text-caption text--secondary">{{ $t('completed') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h2 error--text">{{ statistics.failed_imports }}</div>
            <div class="text-caption text--secondary">{{ $t('failed') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- File Upload Records Table -->
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-history</v-icon>
        {{ $t('file_upload_records') }}
        <v-spacer></v-spacer>
        <div class="d-flex align-center">
          <span class="text-caption mr-2">{{ $t('auto_refresh') }}:</span>
          <v-switch
            v-model="autoRefresh"
            dense
            hide-details
            @change="toggleAutoRefresh">
          </v-switch>
        </div>
      </v-card-title>
      
      <v-card-text class="pa-0">
        <route-plan-file-upload-list ref="fileUploadList" @row-click="showFileUploadDetails" @show-details="showFileUploadDetails" @reprocess="handleReprocess" />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import RoutePlanFileUploadList from '@/components/route-plan/RoutePlanFileUploadList'
import RoutePlanFileUploadDetailModal from '@/components/route-plan/RoutePlanFileUploadDetailModal'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'

export default {
  name: 'RoutePlanFileUploadHistory',
  components: {
    RoutePlanFileUploadList,
    RoutePlanFileUploadDetailModal
  },
  mixins: [
    CurrentUserMixin,
    BrowserUtilitiesMixin,
    ApiUtilitiesMixin
  ],
  data () {
    return {
      loading: false,
      statistics: null,
      autoRefresh: false,
      refreshInterval: null
    }
  },
  methods: {
    async loadStatistics () {
      try {
        const response = await this.$httpAdapter.get('route-plan-file-upload/stats', {
          params: { customer_id: this.customerContext.id }
        })
        this.statistics = response.data
      } catch (error) {
        console.error('Error loading file upload statistics:', error)
        this.$toast.error(this.$t('failed_to_load_statistics'))
      }
    },

    async refreshData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadStatistics(),
          // loadRemoteData() reloads immediately and returns a promise (so this
          // Promise.all actually waits for it), unlike the debounced refreshResults().
          this.$refs.fileUploadList ? this.$refs.fileUploadList.loadRemoteData() : Promise.resolve()
        ])
      } catch (error) {
        console.error('Error refreshing data:', error)
      } finally {
        this.loading = false
      }
    },

    showFileUploadDetails (fileUploadRecord) {
      this.$refs.detailModal.show(fileUploadRecord.id)
    },

    handleReprocess (fileUploadId) {
      // Optimistically flip the row to "processing" right away — the re-fetch
      // below can briefly read a stale "failed" from the read replica.
      if (this.$refs.fileUploadList) {
        this.$refs.fileUploadList.markProcessing(fileUploadId)
      }
      // Reconcile with the server and refresh the stat cards (same as Refresh button).
      this.refreshData()
    },

    toggleAutoRefresh () {
      if (this.autoRefresh) {
        this.startAutoRefresh()
      } else {
        this.stopAutoRefresh()
      }
    },

    startAutoRefresh () {
      // Refresh every 30 seconds
      this.refreshInterval = setInterval(() => {
        this.refreshData()
      }, 30000)
    },

    stopAutoRefresh () {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    },

    customerContextChangeHandler () {
      this.refreshData()
    }
  },

  mounted () {
    this.updateBrowserTitle(this.$t('route_plan_file_upload_history'))
    this.loadStatistics()
  },

  beforeDestroy () {
    this.stopAutoRefresh()
  }
}
</script>

<style scoped>
/* Only apply hover effects to statistic cards, not all v-cards */
.v-row .v-card {
  transition: all 0.3s ease;
}

.v-row .v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08) !important;
}
</style>